import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

// ─── Constants ────────────────────────────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_BACKEND_API_URL_LOCAL + "lightning/stats/";

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

// Data before this date is not required — the date filters never go earlier.
const MIN_DATE = "2026-01-01";
const clampToMinDate = (dateStr) => (dateStr < MIN_DATE ? MIN_DATE : dateStr);

// Updated Tier Colors with exact RGB values from your request
const TIER_COLORS = [
  { color: "#94cb55", border: "#16a34a", label: "Low" },      // Green
  { color: "#f3eb12", border: "#d97706", label: "Medium" },   // Yellow
  { color: "#f39b3a", border: "#dc2626", label: "High" },     // Orange
  { color: "#e62d3a", border: "#b91c1c", label: "Critical" }, // Red
];

function getTierStyle(value, min, max) {
  if (max === min || value === 0) return { fillColor: "#e5e7eb", color: "#d1d5db", fillOpacity: 0.4 };
  const r = (value - min) / (max - min);
  const tier = r < 0.25 ? TIER_COLORS[0] : r < 0.5 ? TIER_COLORS[1] : r < 0.75 ? TIER_COLORS[2] : TIER_COLORS[3];
  return { fillColor: tier.color, color: tier.border, fillOpacity: 0.65, weight: 1.5 };
}

// ─── Calendar Icon SVG ────────────────────────────────────────────────────────
const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// ─── Date Picker with Calendar Icon ──────────────────────────────────────────
// Native <input type="date"> — keeps direct typing/selection of day, month,
// and year. min={MIN_DATE} hides 2025-and-earlier: the browser disables
// picking any day before it and stops backward month/year navigation there.
const DatePickerField = ({ label, value, onChange }) => {
  const inputRef = useRef(null);

  const handleIconClick = () => {
    if (inputRef.current) {
      inputRef.current.showPicker?.();
      inputRef.current.focus();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </span>
      <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        <input
          ref={inputRef}
          type="date"
          value={value}
          min={MIN_DATE}
          max={today()}
          onChange={onChange}
          style={{
            padding: "7px 34px 7px 11px",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            fontSize: "13px",
            background: "#f8fafc",
            color: "#1e293b",
            outline: "none",
            cursor: "pointer",
            appearance: "none",
            WebkitAppearance: "none",
            width: "148px",
          }}
        />
        <button
          type="button"
          onClick={handleIconClick}
          tabIndex={-1}
          style={{
            position: "absolute",
            right: "8px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            padding: "0",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <CalendarIcon />
        </button>
      </div>
    </div>
  );
};

// ─── Stat Badge ───────────────────────────────────────────────────────────────
const StatBadge = ({ label, value, color }) => (
  <div style={{
    background: "white",
    border: `2px solid ${color}`,
    borderRadius: "10px",
    padding: "8px 14px",
    minWidth: "120px",
    boxShadow: `0 2px 6px ${color}25`,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  }}>
    <span style={{ fontSize: "20px", fontWeight: 800, color, lineHeight: 1.1 }}>
      {value != null ? value.toLocaleString() : "—"}
    </span>
    <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const LightningStatsDashboard = () => {
  const [fromDate, setFromDate] = useState(clampToMinDate(monthAgo()));
  const [toDate, setToDate] = useState(today());
  const [submitted, setSubmitted] = useState({ from: clampToMinDate(monthAgo()), to: today() });
  const [loading, setLoading] = useState(false);

  const [countryStats, setCountryStats] = useState(null);
  const [munStats, setMunStats] = useState([]);
  const [subStats, setSubStats] = useState([]);
  const [sucoStats, setSucoStats] = useState([]);
  const [rawData, setRawData] = useState([]);

  const [selectedMun, setSelectedMun] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);

  const [adm1Geo, setAdm1Geo] = useState(null);
  const [adm2Geo, setAdm2Geo] = useState(null);
  const [adm3Geo, setAdm3Geo] = useState(null);

  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const layersRef = useRef([]);

  // Table States (Same as NowcastTable)
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [visibleColumns, setVisibleColumns] = useState({
    adm1: true,
    adm2: true,
    adm3: true,
    pulse_type: true,
    peak_amplitude: true,
    number_sensors: true,
    lat: true,
    lng: true,
    date_hr: true,
  });

  // ── Load GeoJSON once ──
  useEffect(() => {
    (async () => {
      try {
        const [a1, a2, a3] = await Promise.all([
          fetch("/DATA/OSMbased_adm1_boundary.json").then(r => r.json()),
          fetch("/DATA/OSMbased_adm2_boundary.json").then(r => r.json()),
          fetch("/DATA/OSMbased_adm3_boundary.json").then(r => r.json()),
        ]);
        setAdm1Geo(a1);
        setAdm2Geo(a2);
        setAdm3Geo(a3);
      } catch (err) {
        console.error("GeoJSON load error:", err);
      }
    })();
  }, []);

  // ── Init Leaflet map ──
  useEffect(() => {
    if (!mapRef.current || leafletMap.current) return;
    leafletMap.current = L.map(mapRef.current, {
      center: [-8.8742, 125.7275],
      zoom: 7,
      zoomControl: true,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(leafletMap.current);
  }, []);

  // ── Fetch data ──
  const fetchAll = useCallback(async ({ from, to }) => {
    setLoading(true);
    try {
      const [c, m, raw] = await Promise.all([
        axios.get(`${BASE_URL}?from_date=${from}&to_date=${to}&level=0`),
        axios.get(`${BASE_URL}?from_date=${from}&to_date=${to}&level=1`),
        axios.get(`${BASE_URL}?from_date=${from}&to_date=${to}`),
      ]);
      setCountryStats((c.data || [])[0] || null);
      setMunStats(m.data || []);
      setRawData(raw.data || []);
      setSelectedMun(null);
      setSelectedSub(null);
      setSubStats([]);
      setSucoStats([]);
    } catch (e) {
      console.error("Fetch error:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(submitted); }, [submitted, fetchAll]);

  useEffect(() => {
    if (!selectedMun) { setSubStats([]); setSucoStats([]); setSelectedSub(null); return; }
    const { from, to } = submitted;
    axios.get(`${BASE_URL}?from_date=${from}&to_date=${to}&level=2&adm1code=${selectedMun.adm1_code}`)
      .then(r => { setSubStats(r.data || []); setSucoStats([]); setSelectedSub(null); })
      .catch(e => console.error(e));
  }, [selectedMun, submitted]);

  useEffect(() => {
    if (!selectedSub) { setSucoStats([]); return; }
    const { from, to } = submitted;
    axios.get(`${BASE_URL}?from_date=${from}&to_date=${to}&level=3&adm2code=${selectedSub.adm2_code}`)
      .then(r => setSucoStats(r.data || []))
      .catch(e => console.error(e));
  }, [selectedSub, submitted]);

  // ── Draw map layers ── (unchanged)
  useEffect(() => {
    if (!leafletMap.current) return;

    layersRef.current.forEach(l => { try { leafletMap.current.removeLayer(l); } catch (_) {} });
    layersRef.current = [];

    if (adm1Geo) {
      const totals = munStats.map(d => d.total || 0);
      const minV = totals.length ? Math.min(...totals) : 0;
      const maxV = totals.length ? Math.max(...totals) : 0;

      const adm1Layer = L.geoJSON(adm1Geo, {
        style: feat => {
          const code = feat.properties.ADM_Code;
          const stat = munStats.find(d => d.adm1_code === code);
          if (!stat || stat.total === 0) {
            return { fillColor: "#e5e7eb", color: "#94a3b8", fillOpacity: 0.35, weight: 1.2 };
          }
          return { ...getTierStyle(stat.total, minV, maxV), weight: 1.4 };
        },
        onEachFeature: (feat, lyr) => {
          const code = feat.properties.ADM_Code;
          const name = feat.properties.ADM1;
          const stat = munStats.find(d => d.adm1_code === code);

          lyr.bindPopup(`
            <div style="font-family:Arial,sans-serif;font-size:13px;max-width:230px">
              <h3 style="margin:0 0 4px;font-size:14px">${name}</h3>
              <hr style="margin:6px 0;border:0;border-top:1px solid #eee"/>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px;font-size:12px">
                <span style="color:#64748b">Total:</span>
                <span style="font-weight:700;color:#0d6efd">${stat?.total?.toLocaleString() || 0}</span>
                <span style="color:#64748b">CG:</span>
                <span style="font-weight:700;color:#ef4444">${stat?.cg_count?.toLocaleString() || 0}</span>
                <span style="color:#64748b">IC:</span>
                <span style="font-weight:700;color:#22c55e">${stat?.ic_count?.toLocaleString() || 0}</span>
              </div>
              <div style="font-size:11px;color:#94a3b8;margin-top:6px;text-align:center">Click to drill down</div>
            </div>
          `);

          lyr.on({
            mouseover: e => { e.target.setStyle({ weight: 2.5, color: "#0d6efd", fillOpacity: stat ? 0.85 : 0.5 }); e.target.openPopup(); },
            mouseout: e => { adm1Layer.resetStyle(e.target); e.target.closePopup(); },
            click: () => { if (stat) setSelectedMun({ adm1_code: stat.adm1_code, adm1: stat.adm1 }); },
          });
        },
      }).addTo(leafletMap.current);
      layersRef.current.push(adm1Layer);
    }

    if (selectedMun && adm2Geo) {
      const totals = subStats.map(d => d.total || 0);
      const minV = totals.length ? Math.min(...totals) : 0;
      const maxV = totals.length ? Math.max(...totals) : 0;

      const filteredAdm2Features = {
        ...adm2Geo,
        features: adm2Geo.features.filter(f => f.properties.ADM1 === selectedMun.adm1)
      };

      const adm2Layer = L.geoJSON(filteredAdm2Features, {
        style: feat => {
          const code = feat.properties.ADM_Code;
          const stat = subStats.find(d => d.adm2_code === code);
          if (!stat || stat.total === 0) return { fillColor: "#ffffff", color: "#94a3b8", fillOpacity: 0.3, weight: 1.4 };
          return { ...getTierStyle(stat.total, minV, maxV), weight: 1.4 };
        },
        onEachFeature: (feat, lyr) => {
          const code = feat.properties.ADM_Code;
          const name = feat.properties.ADM2;
          const stat = subStats.find(d => d.adm2_code === code);

          lyr.bindPopup(`
            <div style="font-family:Arial,sans-serif;font-size:13px;max-width:230px">
              <h3 style="margin:0 0 4px;font-size:14px">${name}</h3>
              <hr style="margin:6px 0;border:0;border-top:1px solid #eee"/>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px;font-size:12px">
                <span style="color:#64748b">Total:</span>
                <span style="font-weight:700;color:#0d6efd">${stat?.total?.toLocaleString() || 0}</span>
                <span style="color:#64748b">CG:</span>
                <span style="font-weight:700;color:#ef4444">${stat?.cg_count?.toLocaleString() || 0}</span>
                <span style="color:#64748b">IC:</span>
                <span style="font-weight:700;color:#22c55e">${stat?.ic_count?.toLocaleString() || 0}</span>
              </div>
              ${!selectedSub ? `<div style="font-size:11px;color:#94a3b8;margin-top:6px;text-align:center">Click to drill down</div>` : ""}
            </div>
          `);

          lyr.on({
            mouseover: e => { e.target.setStyle({ weight: 2.5, fillOpacity: stat ? 0.85 : 0.5 }); e.target.openPopup(); },
            mouseout: e => { adm2Layer.resetStyle(e.target); e.target.closePopup(); },
            click: () => { if (stat) setSelectedSub({ adm2_code: stat.adm2_code, adm2: stat.adm2 }); },
          });
        },
      }).addTo(leafletMap.current);
      layersRef.current.push(adm2Layer);

      try { leafletMap.current.fitBounds(adm2Layer.getBounds(), { padding: [20, 20] }); } catch (_) {}
    }

    if (selectedSub && adm3Geo) {
      const totals = sucoStats.map(d => d.total || 0);
      const minV = totals.length ? Math.min(...totals) : 0;
      const maxV = totals.length ? Math.max(...totals) : 0;

      const filteredAdm3Features = {
        ...adm3Geo,
        features: adm3Geo.features.filter(f => f.properties.ADM2 === selectedSub.adm2)
      };

      const adm3Layer = L.geoJSON(filteredAdm3Features, {
        style: feat => {
          const code = feat.properties.ADM_Code;
          const stat = sucoStats.find(d => d.adm3_code === code);
          if (!stat || stat.total === 0) return { fillColor: "#ffffff", color: "#94a3b8", fillOpacity: 0.3, weight: 1.4 };
          return { ...getTierStyle(stat.total, minV, maxV), weight: 1.4 };
        },
        onEachFeature: (feat, lyr) => {
          const code = feat.properties.ADM_Code;
          const name = feat.properties.ADM3;
          const stat = sucoStats.find(d => d.adm3_code === code);

          lyr.bindPopup(`
            <div style="font-family:Arial,sans-serif;font-size:13px;max-width:230px">
              <h3 style="margin:0 0 4px;font-size:14px">${name}</h3>
              <hr style="margin:6px 0;border:0;border-top:1px solid #eee"/>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px;font-size:12px">
                <span style="color:#64748b">Total:</span>
                <span style="font-weight:700;color:#0d6efd">${stat?.total?.toLocaleString() || 0}</span>
                <span style="color:#64748b">CG:</span>
                <span style="font-weight:700;color:#ef4444">${stat?.cg_count?.toLocaleString() || 0}</span>
                <span style="color:#64748b">IC:</span>
                <span style="font-weight:700;color:#22c55e">${stat?.ic_count?.toLocaleString() || 0}</span>
              </div>
            </div>
          `);

          lyr.on({
            mouseover: e => { e.target.setStyle({ weight: 2.5, fillOpacity: stat ? 0.85 : 0.5 }); e.target.openPopup(); },
            mouseout: e => { adm3Layer.resetStyle(e.target); e.target.closePopup(); },
          });
        },
      }).addTo(leafletMap.current);
      layersRef.current.push(adm3Layer);

      try { leafletMap.current.fitBounds(adm3Layer.getBounds(), { padding: [20, 20] }); } catch (_) {}
    } else if (!selectedMun && adm1Geo) {
      try {
        const adm1Layer = layersRef.current[0];
        if (adm1Layer) leafletMap.current.fitBounds(adm1Layer.getBounds(), { padding: [20, 20] });
      } catch (_) {}
    }
  }, [munStats, subStats, sucoStats, selectedMun, selectedSub, adm1Geo, adm2Geo, adm3Geo]);

  // ── Chart ──
  // Show every place returned by the API, including those with zero strikes,
  // so their name still appears on the axis with a 0-count bar instead of
  // being silently dropped.
  const chartStats = selectedSub ? sucoStats : selectedMun ? subStats : munStats;
  const filteredChartStats = chartStats;

  const chartOptions = useMemo(() => ({
    chart: { type: "bar", backgroundColor: "transparent", height: 420, marginLeft: 120 },
    title: { text: null },
    xAxis: {
      categories: filteredChartStats.map(d => d.adm3 || d.adm2 || d.adm1 || ""),
      labels: { style: { color: "#334155", fontSize: "12px", fontWeight: "500" } },
    },
    yAxis: { title: { text: "Strike Count" } },
    series: [
      { name: "Total", data: filteredChartStats.map(d => d.total || 0), color: "#0d6efd" },
      { name: "CG (Cloud-to-Ground)", data: filteredChartStats.map(d => d.cg_count || 0), color: "#ef4444" },
      { name: "IC (Intra-Cloud)", data: filteredChartStats.map(d => d.ic_count || 0), color: "#22c55e" },
    ],
    credits: { enabled: false },
  }), [filteredChartStats]);

  const cgRatio = countryStats && countryStats.total > 0
    ? ((countryStats.cg_count / countryStats.total) * 100).toFixed(1) + "%"
    : "—";

  const levelLabel = selectedSub
    ? `Sucos in ${selectedSub.adm2}`
    : selectedMun
      ? `Sub-municipalities in ${selectedMun.adm1}`
      : "Municipalities — Timor-Leste";

  // ── Table Filtering & Pagination ──
  const filteredTableData = useMemo(() => {
    let data = [...rawData];

    const searchLower = searchTerm.toLowerCase();
    data = data.filter(row =>
      row.adm1?.toLowerCase().includes(searchLower) ||
      row.adm2?.toLowerCase().includes(searchLower) ||
      row.adm3?.toLowerCase().includes(searchLower) ||
      row.pulse_type?.toLowerCase().includes(searchLower) ||
      String(row.peak_amplitude || '').includes(searchLower) ||
      row.date_hr?.toLowerCase().includes(searchLower)
    );

    if (sortConfig.key) {
      data.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [rawData, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredTableData.length / rowsPerPage);
  const paginatedData = filteredTableData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    });
  };

  const exportToCSV = () => {
    const headers = Object.keys(visibleColumns).filter(k => visibleColumns[k]);
    const csvContent = [
      headers.join(","),
      ...filteredTableData.map(row =>
        headers.map(h => `"${(row[h] || "").toString().replace(/"/g, '""')}"`).join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lightning_observations_${submitted.from}_to_${submitted.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ fontFamily: "inherit", padding: "0", marginBottom: "20px" }}>

      {/* Control Bar */}
      <div style={{ padding: "12px 20px", backgroundColor: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1px solid #f1f3f5", marginBottom: "16px" }}>
        <style>{`input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0; }`}</style>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <DatePickerField label="From" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            <DatePickerField label="To" value={toDate} onChange={e => setToDate(e.target.value)} />
            <button
              onClick={() => { if (fromDate && toDate) setSubmitted({ from: clampToMinDate(fromDate), to: toDate }); }}
              disabled={loading}
              style={{ padding: "8px 18px", background: loading ? "#94a3b8" : "#0d6efd", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", marginTop: "18px" }}
            >
              {loading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null} {loading ? "Loading…" : "Search"}
            </button>
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", flex: 1 }}>
            <StatBadge label="Total Strikes" value={countryStats?.total} color="#0d6efd" />
            <StatBadge label="Cloud-to-Ground" value={countryStats?.cg_count} color="#ef4444" />
            <StatBadge label="Intra-Cloud" value={countryStats?.ic_count} color="#22c55e" />
            <div style={{ background: "white", border: "2px solid #a855f7", borderRadius: "10px", padding: "8px 14px", minWidth: "90px", boxShadow: "0 2px 6px #a855f725", display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "20px", fontWeight: 800, color: "#a855f7" }}>{cgRatio}</span>
              <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>CG Ratio</span>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={{ padding: "8px 16px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
        <button onClick={() => { setSelectedMun(null); setSelectedSub(null); }} style={{ background: "none", border: "none", color: "#0d6efd", cursor: "pointer", fontWeight: selectedMun ? 400 : 700 }}>Timor-Leste</button>
        {selectedMun && (
          <>
            <span style={{ color: "#94a3b8" }}>›</span>
            <button onClick={() => setSelectedSub(null)} style={{ background: "none", border: "none", color: "#0d6efd", cursor: "pointer", fontWeight: selectedSub ? 400 : 700 }}>{selectedMun.adm1}</button>
          </>
        )}
        {selectedSub && (
          <>
            <span style={{ color: "#94a3b8" }}>›</span>
            <span style={{ fontWeight: 700, color: "#1e293b" }}>{selectedSub.adm2}</span>
          </>
        )}
        {(selectedMun || selectedSub) && (
          <button onClick={() => selectedSub ? setSelectedSub(null) : setSelectedMun(null)} style={{ marginLeft: "6px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "6px", color: "#64748b", cursor: "pointer", fontSize: "12px", padding: "3px 8px" }}>← Back</button>
        )}
        <span style={{ marginLeft: "auto", fontSize: "11px", color: "#94a3b8" }}>{levelLabel}{!selectedSub && " — click a region to drill down"}</span>
      </div>

      {/* Map + Chart */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
        {/* Map with Updated Legend */}
        <div style={{ background: "white", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "14px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {levelLabel}
            </span>

            {/* Updated Legend with Descriptions */}
            <div style={{ display: "flex", flexDirection: "row", gap: "8px", fontSize: "11.5px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "14px", height: "14px", background: "#94cb55", border: "1px solid #16a34a", borderRadius: "3px" }} />
                {/* <span><strong>No immediate threat.</strong> Normal conditions.</span> */}
                <span>Low</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "14px", height: "14px", background: "#f3eb12", border: "1px solid #d97706", borderRadius: "3px" }} />
                {/* <span><strong>Be aware.</strong> Possible threat. Stay alert for updates.</span> */}
                <span>Medium</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "14px", height: "14px", background: "#f39b3a", border: "1px solid #dc2626", borderRadius: "3px" }} />
                {/* <span><strong>Be prepared.</strong> Potential danger. Action may be needed.</span> */}
                <span>High</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "14px", height: "14px", background: "#e62d3a", border: "1px solid #b91c1c", borderRadius: "3px" }} />
                {/* <span><strong>Take action.</strong> Dangerous situation. Immediate response required.</span> */}
                <span>Very High</span>

              </div>
            </div>
          </div>
          <div ref={mapRef} style={{ height: "430px", borderRadius: "10px", overflow: "hidden", border: "1px solid #e2e8f0" }} />
        </div>

        {/* Chart */}
        <div style={{ background: "white", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "14px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <span style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "10px" }}>
            Strike Distribution
          </span>
          {filteredChartStats.length > 0 ? (
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          ) : (
            <div style={{ height: "430px", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "13px", flexDirection: "column", gap: "8px" }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#e2e8f0">
                <path d="M13 2 L3 14 L12 14 L11 22 L21 10 L12 10 L13 2 Z" />
              </svg>
              {loading ? "Loading data…" : "Click a region on the map to explore"}
            </div>
          )}
        </div>
      </div>

      {/* Custom Table - Same style as NowcastTable */}
      <div style={{ background: "white", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
        <div style={{ marginBottom: "12px" }}>
          <span style={{ fontSize: "15px", fontWeight: 700, color: "#1e293b" }}>
            Raw Observations
            <span style={{ marginLeft: "8px", background: "#eff6ff", color: "#0d6efd", borderRadius: "20px", padding: "2px 10px", fontSize: "12px", fontWeight: 600 }}>
              {filteredTableData.length.toLocaleString()} records
            </span>
          </span>
        </div>

        {/* Controls */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "12px", padding: "12px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
          <input
            type="text"
            placeholder="Search by Municipality, Sub-Municipality, Suco..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "13px", flex: "1 1 300px", minWidth: "280px" }}
          />
          <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "13px", background: "white" }}>
            <option value={10}>10 rows</option>
            <option value={15}>15 rows</option>
            <option value={25}>25 rows</option>
            <option value={50}>50 rows</option>
          </select>
          <button onClick={exportToCSV} style={{ padding: "8px 16px", background: "#0d6efd", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
            Export CSV
          </button>

          <div style={{ position: "relative" }}>
            <button
              onClick={() => { const dd = document.getElementById("stats-column-dropdown"); dd.style.display = dd.style.display === "block" ? "none" : "block"; }}
              style={{ padding: "8px 16px", background: "#64748b", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
            >
              Show/Hide Columns
            </button>
            <div id="stats-column-dropdown" style={{ display: "none", position: "absolute", top: "100%", right: 0, marginTop: "4px", background: "white", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 1000, minWidth: "200px" }}>
              {Object.keys(visibleColumns).map(col => (
                <label key={col} style={{ display: "block", padding: "4px 8px", fontSize: "13px", cursor: "pointer" }}>
                  <input type="checkbox" checked={visibleColumns[col]} onChange={e => setVisibleColumns({ ...visibleColumns, [col]: e.target.checked })} style={{ marginRight: "8px" }} />
                  {col === 'adm1' ? 'Municipality' : col === 'adm2' ? 'Sub-Municipality' : col === 'adm3' ? 'Suco' : col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "10px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                {visibleColumns.adm1 && <th onClick={() => handleSort('adm1')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Municipality {sortConfig.key === 'adm1' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
                {visibleColumns.adm2 && <th onClick={() => handleSort('adm2')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Sub-Municipality {sortConfig.key === 'adm2' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
                {visibleColumns.adm3 && <th onClick={() => handleSort('adm3')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Suco {sortConfig.key === 'adm3' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
                {visibleColumns.pulse_type && <th onClick={() => handleSort('pulse_type')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Type {sortConfig.key === 'pulse_type' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
                {visibleColumns.peak_amplitude && <th onClick={() => handleSort('peak_amplitude')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Peak Amplitude {sortConfig.key === 'peak_amplitude' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
                {visibleColumns.number_sensors && <th onClick={() => handleSort('number_sensors')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Sensors {sortConfig.key === 'number_sensors' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
                {visibleColumns.lat && <th onClick={() => handleSort('lat')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Latitude {sortConfig.key === 'lat' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
                {visibleColumns.lng && <th onClick={() => handleSort('lng')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Longitude {sortConfig.key === 'lng' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
                {visibleColumns.date_hr && <th onClick={() => handleSort('date_hr')} style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", cursor: "pointer" }}>Date / Hour {sortConfig.key === 'date_hr' && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={Object.values(visibleColumns).filter(Boolean).length} style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                    No observations found
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    {visibleColumns.adm1 && <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{row.adm1 || "-"}</td>}
                    {visibleColumns.adm2 && <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{row.adm2 || "-"}</td>}
                    {visibleColumns.adm3 && <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{row.adm3 || "-"}</td>}
                    {visibleColumns.pulse_type && (
                      <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>
                        <span style={{ padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, background: row.pulse_type === "cg" ? "#fef2f2" : "#f0fdf4", color: row.pulse_type === "cg" ? "#dc2626" : "#16a34a" }}>
                          {row.pulse_type?.toUpperCase() || "—"}
                        </span>
                      </td>
                    )}
                    {visibleColumns.peak_amplitude && <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{row.peak_amplitude || "-"}</td>}
                    {visibleColumns.number_sensors && <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{row.number_sensors || "-"}</td>}
                    {visibleColumns.lat && <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{row.lat || "-"}</td>}
                    {visibleColumns.lng && <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{row.lng || "-"}</td>}
                    {visibleColumns.date_hr && <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{row.date_hr || "-"}</td>}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ marginTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: "13px", color: "#64748b" }}>
              Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredTableData.length)} of {filteredTableData.length} entries
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: currentPage === 1 ? "#f1f5f9" : "white", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>First</button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: currentPage === 1 ? "#f1f5f9" : "white", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>Previous</button>
              <span style={{ padding: "6px 12px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: currentPage === totalPages ? "#f1f5f9" : "white", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}>Next</button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: currentPage === totalPages ? "#f1f5f9" : "white", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}>Last</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LightningStatsDashboard;